import { Controller } from "@hotwired/stimulus"
import { gsap, MEDIA, scheduleRefresh, scheduleLazyRefresh, startWhenNear } from "./motion"

// Base class for every animation controller on the site.
//
// Subclasses implement `animate(conditions, context)`, which runs inside gsap.matchMedia so
// tweens, timelines, ScrollTriggers and SplitTexts created there are reverted automatically
// when the media query stops matching, on disconnect, and before Turbo snapshots the page
// (otherwise pin-spacers and inline transforms would be cached and duplicated on restore).
//
// Setup is deferred until the element nears the viewport (or the browser is idle) unless the
// subclass sets `static eager = true` or the element sits inside `[data-motion-eager]` —
// use that for anything visible on first paint, where a deferred rewind would flash.
export default class MotionController extends Controller {
  connect() {
    this.cleanups = []
    this.handleBeforeCache = () => this.teardown()
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)

    if (this.constructor.eager || this.element.closest("[data-motion-eager]")) {
      this.start()
    } else {
      this.cancelStart = startWhenNear(this.element, () => this.start({ lazy: true }))
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    this.teardown()
  }

  start({ lazy = false } = {}) {
    if (this.mm) return
    this.cancelStart?.()
    this.cancelStart = null

    this.mm = gsap.matchMedia()
    this.mm.add(MEDIA, (context) => {
      const { desktop, mobile } = context.conditions
      const conditions = { ...context.conditions, motion: desktop || mobile }
      const cleanup = this.animate(conditions, context)
      lazy ? scheduleLazyRefresh() : scheduleRefresh()
      return () => {
        if (typeof cleanup === "function") cleanup()
      }
    })
  }

  teardown() {
    this.cancelStart?.()
    this.cancelStart = null
    this.mm?.revert()
    this.mm = null
    this.cleanups.splice(0).reverse().forEach((fn) => fn())
  }

  // Registers cleanup that must run on disconnect regardless of media state
  // (event listeners, observers, DOM rewrites).
  onTeardown(fn) {
    this.cleanups.push(fn)
  }

  animate() {}
}
